import './dom-globals-guard';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import { reloadForStaleChunk } from './lib/lazyWithReload';
import './app/global.css';
import './app/dark.css';
import './app/i18n';

// ── Sentry (error + performance monitoring) ───────────────────────────────
// The DSN is public/write-only, so it's safe in the bundle. Environment
// (local | staging | production) is what lets us tell these apart in the
// Sentry dashboard; it's set per build via VITE_SENTRY_ENVIRONMENT.
const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

// Only run in built artifacts (staging/prod) with a DSN configured — never on
// the local dev server, and never when explicitly tagged "local". Leaving
// VITE_SENTRY_DSN empty also disables it.
const sentryEnabled =
  Boolean(dsn) && !import.meta.env.DEV && environment !== 'local';

if (sentryEnabled) {
  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance tracing and replays — the same low rates everywhere: at 100 % / 50 % on
    // staging the shared Sentry quota ran out and every page logged a refused send (429,
    // QA 05/10 m73).
    tracesSampleRate: 0.2,
    // Session Replay: sample some normal sessions, always record crashed ones.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
// ──────────────────────────────────────────────────────────────────────────

// Safety net for stale-chunk failures after a redeploy. Vite emits
// 'vite:preloadError' when a dynamically imported chunk can't be fetched (the
// hashed file was replaced by a newer build). Recover with ONE guarded reload,
// shared with lazyWithReload so the two never double-reload.
// Never preventDefault() here: Vite then RESOLVES the import with undefined
// instead of rejecting it, lazyWithReload's catch never runs and React crashes
// on `_result.default` (Sentry ADMIN_ODS-3/-9/-F/-G).
window.addEventListener('vite:preloadError', () => {
  reloadForStaleChunk();
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <Sentry.ErrorBoundary
    fallback={
      <div style={{ padding: 24, textAlign: 'center' }}>
        <h2>Something went wrong.</h2>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    }
  >
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);
